import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@reflex/ui/lib/utils';

/**
 * Shared hover + focus affordance for any clickable circular avatar.
 * Used by the web-local UserAvatar as a profile link. Keep its interaction
 * treatment aligned with the design-system primitive used by plugin surfaces.
 */
const interactiveAvatarClasses =
  'inline-flex rounded-full transition-shadow hover:ring-2 hover:ring-inset hover:ring-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60';

type CommonProps = {
  className?: string;
  title?: string;
  'aria-label'?: string;
  children: React.ReactNode;
};

type LinkProps = CommonProps & {
  to: string;
  onClick?: never;
  type?: never;
};

type ButtonProps = CommonProps & {
  to?: never;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit';
};

export type InteractiveAvatarProps = LinkProps | ButtonProps;

function InteractiveAvatarImpl(
  { className, children, ...rest }: InteractiveAvatarProps,
  ref: React.Ref<HTMLElement>,
) {
  if ('to' in rest && rest.to !== undefined) {
    const { to, ...anchorRest } = rest;
    return (
      <Link
        to={to}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cn(interactiveAvatarClasses, className)}
        {...anchorRest}
      >
        {children}
      </Link>
    );
  }

  const { type = 'button', ...buttonRest } = rest as ButtonProps;
  return (
    <button
      type={type}
      ref={ref as React.Ref<HTMLButtonElement>}
      className={cn(interactiveAvatarClasses, className)}
      {...buttonRest}
    >
      {children}
    </button>
  );
}

export const InteractiveAvatar = React.forwardRef(InteractiveAvatarImpl) as (
  props: InteractiveAvatarProps & { ref?: React.Ref<HTMLElement> },
) => React.ReactElement;
